import Link from 'next/link'
import Image from 'next/image'
import { Linkedin, Twitter, Youtube } from 'lucide-react'

export function LandingFooter() {
  return (
    <footer className="bg-white dark:bg-[#0F1116] text-gray-800 dark:text-white relative overflow-hidden pt-10 sm:pt-16 pb-16 sm:pb-32">
      <div className="absolute inset-0 pointer-events-none hidden sm:block">
        <div className="text-[20rem] sm:text-[40rem] font-bold leading-none text-gray-100 dark:text-[#151820] whitespace-nowrap select-none" style={{ transform: 'translate(-8%, 10%)', }}>
          CareAdvisor
        </div>
      </div>
      
      {/* Top border line */}
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <div className="border-b border-gray-200 dark:border-gray-700 mb-16"></div>
      </div>
      
      <div className="container mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-y-8 gap-x-6 md:gap-16 pb-16 sm:pb-32">
          {/* Company description and social media */}
          <div className="md:col-span-4">
            <p className="text-lg font-light leading-relaxed mb-10 text-gray-700 dark:text-white">
              CareAdvisor is the most complete regulatory compliance AI suite for Minnesota healthcare providers, trusted by hundreds of care facilities statewide.
            </p>
            
            {/* Social media links - moved to left side like Spellbook */}
            <div className="flex items-center">
              <span className="text-sm mr-6 text-gray-600 dark:text-gray-300">Follow us for latest in healthcare compliance AI</span>
              <div className="flex space-x-4">
                <Link href="https://linkedin.com" className="bg-gray-200 dark:bg-white hover:bg-gray-300 dark:hover:bg-gray-100 p-2.5 rounded-full transition-all duration-200">
                  <Linkedin className="h-5 w-5 text-gray-800 dark:text-[#1514C7]" />
                  <span className="sr-only">LinkedIn</span>
                </Link>
                <Link href="https://twitter.com" className="bg-gray-200 dark:bg-white hover:bg-gray-300 dark:hover:bg-gray-100 p-2.5 rounded-full transition-all duration-200">
                  <Twitter className="h-5 w-5 text-gray-800 dark:text-[#1514C7]" />
                  <span className="sr-only">Twitter</span>
                </Link>
                <Link href="https://youtube.com" className="bg-gray-200 dark:bg-white hover:bg-gray-300 dark:hover:bg-gray-100 p-2.5 rounded-full transition-all duration-200">
                  <Youtube className="h-5 w-5 text-gray-800 dark:text-[#1514C7]" />
                  <span className="sr-only">YouTube</span>
                </Link>
              </div>
            </div>
          </div>
          
          {/* Navigation sections with better spacing */}
          <div className="md:col-span-2 md:ml-auto">
            <h3 className="text-gray-900 dark:text-white font-medium mb-5 text-base">Product</h3>
            <nav className="flex flex-col space-y-3">
              <Link href="/chat" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Get Answers</Link>
              <Link href="/plans" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Plans</Link>
              <Link href="/features" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Features</Link>
              <Link href="/compliance-checker" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Compliance Check</Link>
              <Link href="/integrations" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Integrations</Link>
            </nav>
          </div>
          
          <div className="md:col-span-2">
            <h3 className="text-gray-900 dark:text-white font-medium mb-5 text-base">Resources</h3>
            <nav className="flex flex-col space-y-3">
              <Link href="/blog" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Blog</Link>
              <Link href="/success-stories" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Success Stories</Link>
              <Link href="/guidelines" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">MN Guidelines</Link>
              <Link href="/help" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Help Center</Link>
              <Link href="/security" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Security & Privacy</Link>
            </nav>
          </div>
          
          <div className="md:col-span-2">
            <h3 className="text-gray-900 dark:text-white font-medium mb-5 text-base">Company</h3>
            <nav className="flex flex-col space-y-3">
              <Link href="/about" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">About</Link>
              <Link href="/careers" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Careers</Link>
              <Link href="/press" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Press</Link>
              <Link href="/contact" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm transition-colors">Contact</Link>
            </nav>
          </div>
        </div>
      </div>
      
      {/* Refined bottom bar with logo next to copyright */}
      <div className="border-t border-gray-200 dark:border-gray-700 py-8 relative">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 flex flex-col md:flex-row justify-between items-center relative z-10">
          <div className="flex items-center mb-6 md:mb-0">
            <div className="relative h-10 w-10 mr-4">
              <Image
                src="/assets/logo-icon.png"
                alt="CareAdvisor Logo"
                fill
                className="object-contain"
              />
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-300">© {new Date().getFullYear()} CareAdvisor</span>
          </div>
          
          <div className="flex justify-center md:justify-end space-x-6 md:space-x-12 text-xs text-gray-600 dark:text-gray-300">
            <Link href="/privacy" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacy Policy</Link>
            <Link href="/terms" className="hover:text-gray-900 dark:hover:text-white transition-colors">Terms of Service</Link>
            <Link href="/accessibility" className="hover:text-gray-900 dark:hover:text-white transition-colors">Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

